/* Online tracking — where the team is, and what route they walked, for one day.
 *
 * spec/03 **archetype C**: a roster plus one focused subject, one thing hydrated at a time. It is
 * `/visit/:id`'s shape without the moderation verbs, so it is not a fourth archetype. Nothing is
 * sticky — the head is short and carries one action, and the measured-offset apparatus archetype B
 * needs has shipped broken four times.
 *
 * ⚠ THERE IS NO LIVE GPS IN THIS SYSTEM, and the copy must never imply otherwise. A position is
 * the coordinate of the POINT of an OPEN visit; "last" is the point of the last closed one. The
 * mobile app does record a real 30-minute-cadence trail in `Visit.meta`, but it only exists on
 * `GET visits/{id}/` and only lands at visit close, so it is out of scope here. Everything the
 * page derives lives in `derive.js`, checked by `yarn check:tracking`.
 *
 * Built out of parts that already existed: `LeafletMap` (extended with `markers`/`plan`/`fitKey`
 * rather than forked), `PersonCell`/`StatusIcon` from `table-cells`, and — for the planned route —
 * `layersFromRoutes` + `mergedStopsForDate`, the same pure functions the routes editor runs on.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Avatar, Button, Card, Collapse, DatePicker, Empty, Flex, Skeleton, Space, Tag, Typography } from "antd";
import { EnvironmentOutlined, ExclamationCircleOutlined, HistoryOutlined, MinusOutlined, PhoneOutlined, ReloadOutlined, TeamOutlined } from "@ant-design/icons";

import { PersonCell, StatusIcon, dash, fullName, initialsOf } from "../../component/table-cells";
import { SIDE_NAV, TRACKING } from "../../config/lang";
import { getAbortable } from "../../util/action";
import dayjs from "../../util/dayjs";
import { fill, translate } from "../../util/util";
import { layersFromRoutes, mergedStopsForDate } from "../merchandiser/route/routeModel";
import LeafletMap from "../points/ext/LeafletMap";
import { STATES, TONE, activeRows, coordsOf, durationMs, isOpen, planMarkers, planPending, planTrackOf, rosterRows, sequenceMarkers, trackOf } from "./derive";

import "./tracking.scss";

/* `SmallResultsSetPagination.max_page_size` is 100 and the cap is silent — `?page_size=500`
   returns 100 rows with HTTP 200 and no warning. So a whole day is a walk, not one request. */
const PAGE_SIZE = 100;
/* 10 pages = 1000 visits. The bound exists so a very large company cannot hang the page; when it
   bites, the page SAYS SO (see `truncated` below) rather than looking complete. */
const MAX_PAGES = 10;
const POLL_MS = 60000;

const GLYPH = {
  [STATES.IN_VISIT]: EnvironmentOutlined,
  [STATES.OPEN_STALE]: ExclamationCircleOutlined,
  [STATES.BETWEEN]: HistoryOutlined,
  [STATES.IDLE]: MinusOutlined,
};

const STATE_KEY = {
  [STATES.IN_VISIT]: "st_in_visit",
  [STATES.OPEN_STALE]: "st_open_stale",
  [STATES.BETWEEN]: "st_between",
  [STATES.IDLE]: "st_idle",
};

/* Local calendar day. The server runs USE_TZ=False, so a local Y-M-D is what its date filters
   compare against. Copied rather than imported from `visit/List.js`: that import would drag
   `visits.scss` into this bundle. */
const isoDay = date => date.format("YYYY-MM-DD");

const hhmm = value => (value ? dayjs(value).format("HH:mm") : null);

/* ⚠ Round-trip `d` and `m` SYMMETRICALLY. /answers shipped a bug where one direction omitted a
   value the other read back with a different default, so a workspace opened from one segment
   stepped a different queue. `d` is always written; `m` absent means "whole team" in both
   directions. */
const searchToState = search => {
  const params = new URLSearchParams(search);
  const raw = params.get("d");
  const parsed = raw ? dayjs(raw, "YYYY-MM-DD", true) : null;
  const id = params.get("m");
  return {
    day: parsed && parsed.isValid() ? parsed : dayjs(),
    selected: id && Number.isFinite(Number(id)) ? Number(id) : null,
  };
};

const stateToSearch = (day, selected) => {
  const params = new URLSearchParams();
  params.set("d", isoDay(day));
  if (selected !== null && selected !== undefined) params.set("m", String(selected));
  return params.toString();
};

/**
 * Walk a DRF page sequence, bounded.
 *
 * ⚠ Does NOT follow `next`: it is an absolute URL and `getAbortable` prepends `API_URL_PREFIX`.
 * Every other caller in `src/` treats `next` as a boolean and increments `page`, and so does this.
 * `capped` is true when the bound stopped the walk short of `count` — the page must say so.
 */
async function walk(base, signal, maxPages = MAX_PAGES) {
  const rows = [];
  let total = 0;

  for (let page = 1; page <= maxPages; page += 1) {
    const response = await getAbortable(`${base}&page=${page}`, signal);
    if (!response) break; /* the 401 path resolves null */
    rows.push(...(response.results || []));
    total = Number.isFinite(response.count) ? response.count : rows.length;
    if (!response.next) break;
  }

  return { rows, total, capped: total > rows.length };
}

/* Click-to-call. A real `tel:` link, so it dials on a phone and hands off to the desktop's
   handler otherwise — not a button that copies a number. `stopPropagation` because the map head's
   and the rail's copies sit inside clickable areas, where a bare click would also toggle the
   Collapse panel underneath. */
function PhoneLink({ phone, t }) {
  if (!phone) return null;
  const dial = String(phone).replace(/[^+\d]/g, "");
  if (!dial) return null;
  return (
    <Typography.Link href={`tel:${dial}`} className="ot-phone" onClick={event => event.stopPropagation()} aria-label={`${t("call")} ${phone}`}>
      <PhoneOutlined />
      <span>{phone}</span>
    </Typography.Link>
  );
}

function Duration({ visit, now, t }) {
  const ms = durationMs(visit, now);
  if (ms === null) return dash;
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return <>{fill(t("dur_m"), { n: minutes })}</>;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return (
    <>
      {fill(t("dur_h"), { n: hours })}
      {rest ? ` ${fill(t("dur_m"), { n: rest })}` : ""}
    </>
  );
}

/* One merchandiser's day, inside the expanded rail row. Deliberately a plain list and not a
   <Table>: seven columns do not fit a 320px rail, and the map beside it carries the geography. */
function Sequence({ row, plan, planLoading, now, t, onOpenVisit }) {
  const pending = useMemo(() => planPending(plan, row.sequence), [plan, row.sequence]);
  const phone = <PhoneLink phone={row.user.phone_number} t={t} />;

  if (!row.sequence.length) {
    return (
      <Flex vertical gap={4} className="ot-seq">
        {phone}
        <Typography.Text type="secondary" className="ot-empty-line">
          {t("st_idle")}
        </Typography.Text>
      </Flex>
    );
  }

  return (
    <Flex vertical gap={4} className="ot-seq">
      {phone}
      <Typography.Text type="secondary" className="ot-seq__head">
        {t("seq_title")}
      </Typography.Text>

      {row.sequence.map((visit, index) => {
        const point = visit.point || {};
        const open = isOpen(visit);
        return (
          <button type="button" key={visit.id} className="ot-stop" onClick={() => onOpenVisit(visit)}>
            <span className={`ot-stop__n${open ? " ot-stop__n--here" : ""}`}>{index + 1}</span>
            <span className="ot-stop__body">
              <span className="ot-stop__name">{point.name || dash}</span>
              <span className="ot-stop__meta">
                {hhmm(visit.start)}
                {open ? " · " : ` – ${hhmm(visit.end)} · `}
                <Duration visit={visit} now={now} t={t} />
              </span>
              <span className="ot-stop__tags">
                {open ? <Tag color="processing">{t("here_now")}</Tag> : null}
                <Tag className={visit.is_planned ? "ot-tag--on" : "ot-tag--off"}>{visit.is_planned ? t("on_route") : t("off_route")}</Tag>
                {coordsOf(point) ? null : <Tag className="ot-tag--warn">{t("no_coords")}</Tag>}
              </span>
            </span>
          </button>
        );
      })}

      {planLoading ? <Skeleton active paragraph={{ rows: 1 }} title={false} /> : null}

      {!planLoading && plan && !plan.length ? (
        <Typography.Text type="secondary" className="ot-empty-line">
          {t("no_plan")}
        </Typography.Text>
      ) : null}

      {pending.length ? (
        <>
          <Typography.Text type="secondary" className="ot-seq__head">
            {t("plan_pending")}
          </Typography.Text>
          {pending.map(stop => (
            <span key={stop.point.id} className="ot-stop ot-stop--plan">
              <span className="ot-stop__n ot-stop__n--plan" aria-hidden="true" />
              <span className="ot-stop__body">
                <span className="ot-stop__name">{stop.point.name || dash}</span>
              </span>
            </span>
          ))}
        </>
      ) : null}
    </Flex>
  );
}

export default function Tracking() {
  const lang = useSelector(state => state.me?.language);
  const history = useHistory();
  const location = useLocation();

  const t = useCallback(key => translate(TRACKING, key, lang), [lang]);

  const initial = useMemo(() => searchToState(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [day, setDay] = useState(initial.day);
  const [selected, setSelected] = useState(initial.selected);

  const [team, setTeam] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitTotal, setVisitTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(null);
  const [tick, setTick] = useState(0);

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const planCache = useRef(new Map());

  const dayKey = isoDay(day);
  const isToday = dayKey === isoDay(dayjs());

  /* The filters ARE the query string: a view is shareable and survives a reload. */
  useEffect(() => {
    const next = stateToSearch(day, selected);
    if (next !== location.search.replace(/^\?/, "")) history.replace({ pathname: location.pathname, search: `?${next}` });
  }, [dayKey, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  /* The roster is day-independent, so it is fetched once per mount and never on a poll. It is
     needed at all because visit rows cannot answer "no visits today", and dropping an idle
     merchandiser would make the page look complete while hiding who has not started. */
  useEffect(() => {
    const controller = new AbortController();
    walk(`merchandisers/?is_active=true&page_size=${PAGE_SIZE}`, controller.signal)
      .then(result => {
        if (!controller.signal.aborted) setTeam(result.rows);
      })
      .catch(err => {
        if (err?.name !== "AbortError") setTeam([]);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setBusy(true);

    /* `start__lte` is the NEXT day, and the filter is on `start` alone. Filtering `end__*` would
       drop every still-open visit — exactly the rows this page exists for — and there is no
       `end__isnull` to ask for them instead. `ordering=start` is mandatory, not cosmetic: without
       a deterministic order, 100-row windows can duplicate and skip rows between pages. */
    const query = [`start__gte=${dayKey}`, `start__lte=${isoDay(day.add(1, "day"))}`, "ordering=start", `page_size=${PAGE_SIZE}`].join("&");

    walk(`visits/?${query}`, signal)
      .then(result => {
        if (signal.aborted) return;
        setVisits(result.rows);
        setVisitTotal(result.total);
        setCapped(result.capped);
        setError(false);
        setUpdated(Date.now());
        setBusy(false);
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        setError(true);
        setBusy(false);
      });

    return () => controller.abort();
  }, [dayKey, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Auto-refresh, today only — a past day cannot change.
     Re-arms on `updated` rather than on the rows, and does not arm at all while the tab is
     hidden: if the timer fired while hidden it would not tick, `updated` would never change and
     nothing would re-arm, so the cycle stops dead and `visibilitychange` restarts it. A tab left
     open overnight makes zero requests. */
  useEffect(() => {
    if (!isToday || error || document.hidden) return undefined;
    const timer = setTimeout(() => {
      if (!document.hidden) setTick(value => value + 1);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [isToday, updated, error]);

  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) setTick(value => value + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  /* The planned route: one request, only once a merchandiser is selected, cached per person+day. */
  useEffect(() => {
    if (selected === null || selected === undefined) {
      setPlan(null);
      return undefined;
    }

    const key = `${selected}|${dayKey}`;
    const cached = planCache.current.get(key);
    if (cached) {
      setPlan(cached);
      return undefined;
    }

    const controller = new AbortController();
    setPlanLoading(true);

    getAbortable(`routes?user=${selected}&date=${dayKey}`, controller.signal)
      .then(response => {
        if (controller.signal.aborted) return;
        const list = Array.isArray(response) ? response : response?.results || [];
        /* ⚠ FILTER BY THE ROUTE'S OWN USER CLIENT-SIDE, and do not skip this because `?user=` is
           in the URL. picsell-api's base RouteViewSet carries `filter_class` — the attribute
           django-filter dropped in 2.0, while the project pins 2.4.0 — so `?user=` and `?date=`
           were both silently ignored and every route the supervisor created came back, for every
           merchandiser. The `filterset_class` fix is a local, undeployed edit; this line is what
           makes the page correct on either side of that deploy.
           Its queryset is also `created_by | user` scoped, so a route somebody else created is
           invisible — which is why an empty result renders as a neutral "no planned route" rather
           than a claim that the merchandiser is unplanned. */
        const mine = list.filter(route => (route.user?.id ?? route.user) === selected);
        const { layers } = layersFromRoutes(mine);
        const stops = mergedStopsForDate(day.toDate(), layers);
        planCache.current.set(key, stops);
        setPlan(stops);
        setPlanLoading(false);
      })
      .catch(err => {
        if (controller.signal.aborted || err?.name === "AbortError") return;
        setPlan([]);
        setPlanLoading(false);
      });

    return () => controller.abort();
  }, [selected, dayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* `updated` is the moment the data was true, and it is what the head reports — so staleness is
     measured against it rather than against a clock that moves under the render. */
  const now = updated || Date.now();
  const rows = useMemo(() => rosterRows(team || [], visits, now), [team, visits, now]);
  const selectedRow = useMemo(() => rows.find(row => row.user.id === selected) || null, [rows, selected]);

  const markers = useMemo(() => {
    if (selectedRow) {
      /* Plan pins first so the numbered actual route paints over them. */
      return [...planMarkers(plan, selectedRow.sequence), ...sequenceMarkers(selectedRow.sequence)];
    }
    /* At rest the map answers "who is out there NOW", so it carries open visits and nothing else.
       A scatter of last-known pins would read as live positions and would not be. */
    return activeRows(rows)
      .map(row => {
        const at = coordsOf(row.visit && row.visit.point);
        if (!at) return null;
        const name = fullName(row.user);
        return {
          key: `u${row.user.id}`,
          lat: at[0],
          lng: at[1],
          /* The house avatar: the photo when there is one, the INITIALS otherwise — and the
             initials are also what LeafletMap falls back to when an old photo URL 404s, which is
             common on legacy records. Never a generic glyph. */
          label: initialsOf(name),
          photo: row.user.photo || null,
          kind: "who",
          tone: TONE[row.kind],
          clickable: true,
          userId: row.user.id,
          title: `${name} — ${(row.visit.point && row.visit.point.name) || ""}`,
        };
      })
      .filter(Boolean);
  }, [rows, selectedRow, plan]);

  const track = useMemo(() => (selectedRow ? trackOf(selectedRow.sequence) : null), [selectedRow]);
  const planLine = useMemo(() => (selectedRow ? planTrackOf(plan) : null), [selectedRow, plan]);

  /* Fit is re-armed by the DAY, the SELECTION, and whether the PLAN has landed — never by the
     visit data, so a 60-second poll leaves the pan and zoom the user set exactly where they are.
     The plan matters because it arrives one request later than the visits: fitting only on
     selection framed the visited points and left the planned-but-unvisited stop off-screen, with
     its dashed line running out of the corner. The plan is cached per person+day, so re-selecting
     somebody refits once, not twice, and a refresh not at all. */
  const fitKey = `${dayKey}:${selected ?? "all"}:${plan ? plan.length : "-"}`;

  const hasGeometry = markers.length > 0 || (track && track.length > 0) || (planLine && planLine.length > 0);
  const loadingFirst = busy && team === null;

  const stateLine = row => {
    if (row.kind === STATES.IDLE) return t("st_idle");
    const point = (row.visit && row.visit.point && row.visit.point.name) || "";
    return fill(t(STATE_KEY[row.kind]), { point, time: hhmm(row.since ? new Date(row.since).toISOString() : null) || "" });
  };

  const items = rows.map(row => {
    const Icon = GLYPH[row.kind];
    return {
      key: String(row.user.id),
      className: `ot-row ot-row--${row.kind}${row.user.id === selected ? " ot-row--on" : ""}`,
      label: (
        <Flex align="center" gap={8} className="ot-row__label">
          <StatusIcon Icon={Icon} tone={TONE[row.kind]} label={t(STATE_KEY[row.kind])} />
          <Flex vertical className="ot-row__who">
            {/* The count sits on the NAME line, not beside the two-line block: as a third column
                it competed with the name for the rail's 320px and was clipped mid-word. Here both
                lines get the full width and the name ellipsizes on its own. */}
            <Flex align="center" gap={8} className="ot-row__top">
              <PersonCell user={row.user} />
              {row.sequence.length ? (
                <Typography.Text type="secondary" className="ot-row__n">
                  {fill(t("points_visited"), { n: row.sequence.length })}
                </Typography.Text>
              ) : null}
            </Flex>
            <Typography.Text type="secondary" className="ot-row__state" ellipsis={{ tooltip: stateLine(row) }}>
              {stateLine(row)}
            </Typography.Text>
          </Flex>
        </Flex>
      ),
      children: <Sequence row={row} plan={plan} planLoading={planLoading} now={now} t={t} onOpenVisit={visit => history.push(`/visit/${visit.id}`)} />,
    };
  });

  return (
    <Flex vertical gap="middle" className="shell-content lp ot">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "tracking", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>

        <Space align="center" wrap>
          {updated ? (
            <Typography.Text type="secondary" className="ot-updated" aria-live="polite">
              {fill(t("updated"), { time: dayjs(updated).format("HH:mm") })}
            </Typography.Text>
          ) : null}
          <DatePicker value={day} onChange={next => setDay(next || dayjs())} allowClear={false} aria-label={t("day")} format="DD.MM.YYYY" />
          <Button icon={<ReloadOutlined />} onClick={() => setTick(value => value + 1)} loading={busy} aria-label={t("refresh")}>
            {t("refresh")}
          </Button>
        </Space>
      </Flex>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={t("load_error")}
          action={
            <Button size="small" onClick={() => setTick(value => value + 1)}>
              {t("retry")}
            </Button>
          }
        />
      ) : null}

      {/* A bound that bit must SAY so — a silently truncated day reads as a complete one. */}
      {capped ? <Alert type="warning" showIcon message={fill(t("truncated"), { shown: visits.length, total: visitTotal })} /> : null}

      <div className={`ot-body${busy && !loadingFirst ? " ot-body--busy" : ""}`}>
        <Card className="ot-rail" variant="outlined" size="small">
          {loadingFirst ? (
            <Skeleton active paragraph={{ rows: 8 }} title={false} />
          ) : rows.length ? (
            <Collapse
              accordion
              ghost
              items={items}
              activeKey={selected === null || selected === undefined ? [] : [String(selected)]}
              onChange={keys => {
                const key = Array.isArray(keys) ? keys[0] : keys;
                setSelected(key === undefined || key === null ? null : Number(key));
              }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical gap={4}>
                  <Typography.Text strong>{t("empty_roster_title")}</Typography.Text>
                  <Typography.Text type="secondary">{t("empty_roster_desc")}</Typography.Text>
                </Flex>
              }
            />
          )}
        </Card>

        <Card
          className="ot-mapcard"
          variant="outlined"
          title={
            selectedRow ? (
              /* The chosen merchandiser, stated with their face and a way to reach them — the map
                 alone showed a route with no owner, so "who is this" had to be read off the rail. */
              <Flex align="center" gap={10} wrap className="ot-who">
                <Avatar size={32} style={{ fontSize: 13 }} className="lp-who__av" src={selectedRow.user.photo || null}>
                  {initialsOf(fullName(selectedRow.user))}
                </Avatar>
                <Flex vertical className="ot-who__text">
                  <span className="ot-who__name">{fullName(selectedRow.user)}</span>
                  <PhoneLink phone={selectedRow.user.phone_number} t={t} />
                </Flex>
                {planLine && planLine.length >= 2 ? <Tag className="ot-tag--plan">{t("plan_title")}</Tag> : null}
              </Flex>
            ) : null
          }
          extra={
            selectedRow ? (
              <Button size="small" icon={<TeamOutlined />} onClick={() => setSelected(null)}>
                {t("show_team")}
              </Button>
            ) : null
          }>
          {loadingFirst ? (
            <Skeleton.Node active className="ot-map-sk" />
          ) : hasGeometry ? (
            <LeafletMap
              markers={markers}
              track={track}
              plan={planLine}
              fitKey={fitKey}
              zoom={11}
              height="100%"
              failedText={t("map_unavailable")}
              /* Clicking a face on the team map selects that merchandiser — the map is a way in to
                 a person, not only a readout of where they are. */
              onMarkerClick={marker => marker.userId && setSelected(marker.userId)}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical gap={4}>
                  <Typography.Text strong>{t("empty_map_title")}</Typography.Text>
                  <Typography.Text type="secondary">{t("empty_map_desc")}</Typography.Text>
                </Flex>
              }
            />
          )}
        </Card>
      </div>
    </Flex>
  );
}
